import { useEffect, useState } from "react";
import type React from "react";

import { faCircleQuestion, faClockFour } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack, Typography } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { usePostDismissAppointmentSuggestion } from "api/hooks/usePostDismissAppointmentSuggestion";
import { AppointmentToSchedule } from "api/models/Patient";
import type { CeStructureType } from "api/models/PatientProfile";
import { CrossIcon } from "assets";
import { useProfileContext } from "contexts/ProfileContext";
import { BaseButton } from "shared/atoms/BaseButton";
import { Hidden } from "shared/atoms/Hidden";
import { Tooltip } from "shared/atoms/Tooltip";
import { ConfirmPopup } from "shared/molecules/ConfirmPopup";
import { DARK_BLUE } from "theme/colors";
import { AnalyticsEvents, AnalyticsService } from "utils/analytics";
import { getPatientFullDetailsQueryKey } from "utils/contexts/providers/useGetPatientsFullDetailsQuery";

interface Props {
  patientId: number;
  appointmentToSchedule: AppointmentToSchedule;
  ceStructure: CeStructureType;
  isPremiumSignedUp?: boolean;
  isStockholm?: boolean;
  isVGR?: boolean;
}

export const ScheduleCallBox: React.FC<Props> = ({
  patientId,
  appointmentToSchedule,
  ceStructure,
  isPremiumSignedUp = false,
  isStockholm = false,
  isVGR = false,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { profile } = useProfileContext();
  const queryClient = useQueryClient();

  const isAdherence = appointmentToSchedule === AppointmentToSchedule.ADHERENCE;
  const isWarning = isAdherence;

  const getPurpose = () => {
    if (appointmentToSchedule === AppointmentToSchedule.FOLLOW_UP_VIDEO_CALL) {
      return AppointmentToSchedule.FOLLOW_UP;
    }
    if (
      appointmentToSchedule === AppointmentToSchedule.ADHERENCE ||
      appointmentToSchedule === AppointmentToSchedule.FOLLOW_UP_CAP_RESET ||
      appointmentToSchedule === AppointmentToSchedule.FOLLOW_UP_GUIDED_CARE ||
      appointmentToSchedule === AppointmentToSchedule.FOLLOW_UP_RESUME_TREATMENT
    ) {
      return AppointmentToSchedule.GENERAL;
    }

    return appointmentToSchedule;
  };

  const getMedium = () => {
    const videoURLstring = "&meeting_medium=video";

    if ((isStockholm || isVGR) && (AppointmentToSchedule.FOLLOW_UP || AppointmentToSchedule.KICK_OFF)) {
      return videoURLstring;
    }
    if (AppointmentToSchedule.FOLLOW_UP_VIDEO_CALL) {
      return videoURLstring;
    }
    return "";
  };

  const isAppointmentDismissible =
    appointmentToSchedule === AppointmentToSchedule.ADHERENCE ||
    appointmentToSchedule === AppointmentToSchedule.DISCHARGE_CALL ||
    ((isStockholm || isVGR) && appointmentToSchedule === AppointmentToSchedule.FOLLOW_UP) ||
    appointmentToSchedule === AppointmentToSchedule.FOLLOW_UP_CAP_RESET ||
    appointmentToSchedule === AppointmentToSchedule.FOLLOW_UP_GUIDED_CARE ||
    appointmentToSchedule === AppointmentToSchedule.FOLLOW_UP_RESUME_TREATMENT ||
    appointmentToSchedule === AppointmentToSchedule.FOLLOW_UP_VIDEO_CALL ||
    appointmentToSchedule === AppointmentToSchedule.TREATMENT_SWITCHED;

  // Description is displayed for dismissible appointment
  const showDescription = isAppointmentDismissible;

  const [showDismiss, setShowDismiss] = useState(isAppointmentDismissible);
  const [confirmOpen, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const postDismissAppointmentSuggestion = usePostDismissAppointmentSuggestion();
  const initiateDismissAppointment = async () => {
    postDismissAppointmentSuggestion.mutate(
      {
        patientId,
        data: {
          suggestion_type: appointmentToSchedule,
        },
      },
      {
        onSuccess() {
          // invalidate patient query
          if (profile) {
            const queryKey = getPatientFullDetailsQueryKey({
              patientId,
              therapistId: profile.id,
              therapistMarket: profile.market ?? "",
            });
            queryClient.invalidateQueries(queryKey);
          }
          setConfirm(false);
          setShowDismiss(isAppointmentDismissible);
        },
        onError() {
          setShowDismiss(true);
        },
      }
    );
  };

  useEffect(() => {
    if (isAdherence) {
      trackCheckInAction("Notification shown");
    }
  }, [isAdherence]);

  const trackCheckInAction = (action: string): void => {
    AnalyticsService.track(AnalyticsEvents.PATIENTS.CHECK_IN_CALL, {
      therapistId: profile?.id,
      patientId,
      action,
    });
  };

  // FE hack to pass a different appointment for reset patients that were never in Premium
  // Rendering different translations for FOLLOW_UP_RESUME_TREATMENT and FOLLOW_UP_CAP_RESET appointments
  const appointmentToScheduleCustomized =
    appointmentToSchedule === AppointmentToSchedule.FOLLOW_UP_CAP_RESET && !isPremiumSignedUp
      ? AppointmentToSchedule.FOLLOW_UP_RESUME_TREATMENT
      : appointmentToSchedule;

  const lowercaseFirstLetter = (text: string) => (text ? text[0].toLowerCase() + text.slice(1) : "");
  const getAppointmentTypeTranslation = (type: AppointmentToSchedule): string => {
    const key = `messages.appointments.types.${type.toUpperCase()}`;
    return lowercaseFirstLetter(t(key as never));
  };

  const getDescription = () =>
    appointmentToScheduleCustomized === AppointmentToSchedule.FOLLOW_UP_VIDEO_CALL && ceStructure === "rtm"
      ? t("messages.appointments.description.FOLLOW_UP_VIDEO_CALL_RTM")
      : t(`messages.appointments.description.${appointmentToScheduleCustomized.toUpperCase()}` as never);

  const showDismissButton = () => {
    return (
      <>
        <DismissButton
          data-testid="dismiss-appointment-button"
          onClick={event => {
            event.stopPropagation();
            setConfirm(prevState => !prevState);
          }}
          variant="tertiary"
        >
          <Hidden type="aboveTablet">{t(`buttons.dismiss`)}</Hidden>
          <Hidden type="belowTablet">
            <CrossIcon />
          </Hidden>
        </DismissButton>

        <ConfirmPopup
          text={t(`messages.appointments.confirm_dismiss`)}
          visible={confirmOpen}
          dataTestId="confirm-popup"
          close={() => {
            setConfirm(false);
          }}
          onConfirm={() => {
            initiateDismissAppointment();
          }}
          error={error}
          setError={setError}
        />
      </>
    );
  };

  const getMessage = (): JSX.Element => {
    if (isAdherence) {
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle1">{t("messages.appointments.types.LOW_ADHERENCE")}</Typography>
          {showDescription && (
            <Tooltip title={getDescription()} placement="bottom" arrow>
              <FontAwesomeIcon icon={faCircleQuestion} style={{ color: DARK_BLUE[60], fontSize: 16 }} />
            </Tooltip>
          )}
        </Stack>
      );
    }
    const appointmentTypeTranslation = getAppointmentTypeTranslation(appointmentToSchedule);
    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <FontAwesomeIcon icon={faClockFour} style={{ color: "#307FE2" }} />
        <Typography variant="subtitle1">
          {isAppointmentDismissible && appointmentToSchedule !== AppointmentToSchedule.FOLLOW_UP_VIDEO_CALL ? (
            <Trans
              i18nKey="messages.appointments.consider_scheduling_a"
              values={{ appointmentType: appointmentTypeTranslation }}
            />
          ) : (
            <Trans
              i18nKey="messages.appointments.it_is_time"
              values={{ appointmentType: appointmentTypeTranslation }}
            />
          )}
        </Typography>
        {showDescription && (
          <Tooltip title={getDescription()} placement="bottom" arrow>
            <FontAwesomeIcon icon={faCircleQuestion} style={{ color: DARK_BLUE[60], fontSize: 16 }} />
          </Tooltip>
        )}
      </Stack>
    );
  };

  const getActions = (): JSX.Element => {
    const purpose = getPurpose();
    const medium = getMedium();

    if (isAdherence) {
      return (
        <ButtonWrapper>
          <BaseButton
            text={t("messages.message")}
            onClick={() => {
              navigate(`/messages/patients/${patientId}?purpose=${purpose}`);
              trackCheckInAction("Clicked message");
            }}
          />
          <BaseButton
            text={t(`messages.schedule_call`)}
            onClick={() => {
              navigate(`/messages/patients/${patientId}?book=true&meeting_purpose=${purpose}`);
              trackCheckInAction("Clicked schedule call");
            }}
          />
          {showDismiss && showDismissButton()}
        </ButtonWrapper>
      );
    }

    return (
      <ButtonWrapper>
        <BaseButton
          text={t("messages.schedule_call")}
          onClick={() => {
            navigate(`/messages/patients/${patientId}?book=true&meeting_purpose=${purpose}${medium}`);
          }}
        />
        {showDismiss && showDismissButton()}
      </ButtonWrapper>
    );
  };

  return (
    <Container data-testid="schedule-call-box" $isWarning={isWarning}>
      {getMessage()}
      {getActions()}
    </Container>
  );
};

const Container = styled.div<{ $isWarning: boolean }>`
  width: 100%;
  box-sizing: border-box;
  background: ${({ $isWarning, theme }) => ($isWarning ? theme.colors.exercise.background : theme.colors.redesign.b10)};
  border: 1px solid
    ${({ $isWarning, theme }) => ($isWarning ? theme.colors.exercise.accent : theme.colors.redesign.b100)};
  border-radius: 12px;
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  position: relative;

  ${props => props.theme.belowBreakpoint} {
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px !important;
  ${props => props.theme.belowBreakpoint} {
    flex-direction: column;
    justify-content: center;
  }
`;

const DismissButton = styled(BaseButton)`
  align-self: center;
  flex-grow: 0;
  margin: 0 !important;
  flex-basis: auto !important;
  padding: 0 15px;
  width: auto;
  min-width: auto;

  ${props => props.theme.belowBreakpoint} {
    margin: 0 auto;
    flex-grow: 1;
  }
`;
